import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { SheetGeometry, SheetPolygonGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolygonGeometryNew, sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { radianToDegree } from "common/utils/math";
import { percentNew, sheetPointsNew } from "common/utils/nominalNumbers";
import { arc2ByCenterStartAngle } from "geom/arc";
import { TWO_PI } from "geom/core";
import { mat3Mult, mat3Rotate, mat3Scale, mat3Translate } from "geom/mat3";
import { polyLine2ByTwoPoints, polyLine2Transform } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2AngleBetweenCW, vec2Normalize } from "geom/vec2";

/** Matches {@link Load} outlet circle geometry. */
export const SWITCHED_OUTLET_CIRCLE_CY = 8;

/** Matches {@link Load} outlet circle geometry. */
export const SWITCHED_OUTLET_RADIUS = 4;

export const SWITCHED_OUTLET_LINE_TOP = 0;
export const SWITCHED_OUTLET_LINE_BOTTOM = 12;
export const SWITCHED_OUTLET_SLOT_XS = [-1.5, 1.5] as const;

/** Right slot line in symbol space; reads as the left compartment after rotate(180). */
export const SWITCHED_OUTLET_FILLED_SECTION_BOUNDARY_X = SWITCHED_OUTLET_SLOT_XS[1];

export const SWITCHED_OUTLET_CIRCLE_CLIP_ID = "switchedOutletCircleClip";

export const SWITCHED_OUTLET_CIRCLE_SEGMENTS = 16;

function switchedOutletSymbolTransform(
  x: number,
  y: number,
  scale: number,
  normal: [number, number],
  planRotationRads: number,
) {
  const [normalX, normalY] = vec2Normalize(normal[0], normal[1]);
  const angleCW = radianToDegree(vec2AngleBetweenCW(0, 1, normalX, normalY) + planRotationRads);
  const angleRad = (angleCW * Math.PI) / 180;
  return mat3Mult(mat3Translate(x, y), mat3Mult(mat3Rotate(Math.PI + angleRad), mat3Scale(scale, scale)));
}

function switchedOutletSectionBoundaryYExtents(
  radius: number,
  circleCy: number,
  sectionBoundaryX: number,
): { yTop: number; yBottom: number } {
  const dy = Math.sqrt(radius * radius - sectionBoundaryX * sectionBoundaryX);
  return { yTop: circleCy - dy, yBottom: circleCy + dy };
}

/** Gray fill for the switched compartment, clipped to the outlet circle on sheets. */
export function switchedOutletFilledSectionSvgPath(
  radius: number = SWITCHED_OUTLET_RADIUS,
  circleCy: number = SWITCHED_OUTLET_CIRCLE_CY,
  sectionBoundaryX: number = SWITCHED_OUTLET_FILLED_SECTION_BOUNDARY_X,
): string {
  const { yTop, yBottom } = switchedOutletSectionBoundaryYExtents(radius, circleCy, sectionBoundaryX);
  // Small-arc, clockwise arc along the outer edge of the circle.
  return `M ${sectionBoundaryX} ${yTop} A ${radius} ${radius} 0 0 1 ${sectionBoundaryX} ${yBottom} Z`;
}

export function switchedOutletFilledSectionPolygon(
  radius: number = SWITCHED_OUTLET_RADIUS,
  circleCy: number = SWITCHED_OUTLET_CIRCLE_CY,
  sectionBoundaryX: number = SWITCHED_OUTLET_FILLED_SECTION_BOUNDARY_X,
  segments: number = SWITCHED_OUTLET_CIRCLE_SEGMENTS,
): PolyLine2 {
  const dy = Math.sqrt(radius * radius - sectionBoundaryX * sectionBoundaryX);
  const topAngle = Math.atan2(-dy, sectionBoundaryX);
  const bottomAngle = Math.atan2(dy, sectionBoundaryX);
  let ccwSweep = bottomAngle - topAngle;
  if (ccwSweep < 0) {
    ccwSweep += TWO_PI;
  }

  const arc = arc2ByCenterStartAngle(0, circleCy, sectionBoundaryX, circleCy - dy, ccwSweep, 0.01, segments, segments);
  const closed = polyLine2New(arc.length + 2);
  for (let i = 0; i < arc.length; i++) {
    closed[i] = arc[i];
  }
  closed[arc.length] = arc[0];
  closed[arc.length + 1] = arc[1];
  return closed;
}

export function switchedOutletCircleOutline(
  radius: number = SWITCHED_OUTLET_RADIUS,
  circleCy: number = SWITCHED_OUTLET_CIRCLE_CY,
  segments: number = SWITCHED_OUTLET_CIRCLE_SEGMENTS,
): PolyLine2 {
  return arc2ByCenterStartAngle(0, circleCy, radius, circleCy, TWO_PI, 0.01, segments, segments);
}

export interface SwitchedOutletFillSheetGeometryProps {
  x: number;
  y: number;
  scale?: number;
  normal?: [number, number];
  planRotationRads?: number;
  fillDarkColor: string;
  handle?: DocEntityHandle;
}

/** Gray fill for the switched compartment on electrical plan sheets. */
export function switchedOutletFillSheetGeometry(props: SwitchedOutletFillSheetGeometryProps): SheetPolygonGeometry {
  const { x, y, scale = 1, normal = [0, 1], planRotationRads = 0, fillDarkColor, handle } = props;

  const radius = SWITCHED_OUTLET_RADIUS * scale;
  const circleCy = SWITCHED_OUTLET_CIRCLE_CY * scale;
  const sectionBoundaryX = SWITCHED_OUTLET_FILLED_SECTION_BOUNDARY_X * scale;
  const transform = switchedOutletSymbolTransform(x, y, scale, normal, planRotationRads);
  const filledSection = polyLine2Transform(
    switchedOutletFilledSectionPolygon(radius, circleCy, sectionBoundaryX),
    transform,
  );

  return sheetPolygonGeometryNew({
    handle,
    paths: [[filledSection]],
    fillColor: fillDarkColor,
    fillOpacity: percentNew(1),
  });
}

/** Injects switched-outlet fill markup into a load SVG string (legends, inline previews). */
export function withSwitchedOutletFillInLoadSvg(loadSvg: string, fillColor: string): string {
  const ellipseStart = loadSvg.indexOf("<ellipse");
  if (ellipseStart === -1) {
    return loadSvg;
  }

  const selfClosingEnd = loadSvg.indexOf("/>", ellipseStart);
  const explicitCloseTag = loadSvg.indexOf("</ellipse>", ellipseStart);
  let insertAt = -1;
  if (selfClosingEnd !== -1 && (explicitCloseTag === -1 || selfClosingEnd < explicitCloseTag)) {
    insertAt = selfClosingEnd + 2;
  } else if (explicitCloseTag !== -1) {
    insertAt = explicitCloseTag + "</ellipse>".length;
  }
  if (insertAt === -1) {
    return loadSvg;
  }

  const fillMarkup = [
    `<defs><clipPath id="${SWITCHED_OUTLET_CIRCLE_CLIP_ID}">`,
    `<ellipse cx="0" cy="${SWITCHED_OUTLET_CIRCLE_CY}" rx="${SWITCHED_OUTLET_RADIUS}" ry="${SWITCHED_OUTLET_RADIUS}"/>`,
    `</clipPath></defs>`,
    `<path d="${switchedOutletFilledSectionSvgPath()}" clip-path="url(#${SWITCHED_OUTLET_CIRCLE_CLIP_ID})" fill="${fillColor}" stroke="none"/>`,
  ].join("");

  return loadSvg.slice(0, insertAt) + fillMarkup + loadSvg.slice(insertAt);
}

export interface SwitchedOutletSheetGeometryProps {
  x: number;
  y: number;
  scale?: number;
  normal?: [number, number];
  planRotationRads?: number;
  strokeColor?: string;
  fillLightColor?: string;
  fillDarkColor?: string;
  linewidth?: number;
}

/**
 * Native sheet geometry for the switched outlet symbol: circle, gray filled section,
 * and duplex-style vertical slot lines. Matches the SVG in {@link Load}.
 */
export function generateSwitchedOutletSheetGeometry(props: SwitchedOutletSheetGeometryProps): SheetGeometry[] {
  const {
    x,
    y,
    scale = 1,
    normal = [0, 1],
    planRotationRads = 0,
    strokeColor = "black",
    fillLightColor = "white",
    fillDarkColor = "#808080",
    linewidth = 1,
  } = props;

  const radius = SWITCHED_OUTLET_RADIUS * scale;
  const circleCy = SWITCHED_OUTLET_CIRCLE_CY * scale;
  const lineTop = SWITCHED_OUTLET_LINE_TOP * scale;
  const lineBottom = SWITCHED_OUTLET_LINE_BOTTOM * scale;
  const sectionBoundaryX = SWITCHED_OUTLET_FILLED_SECTION_BOUNDARY_X * scale;

  const transform = switchedOutletSymbolTransform(x, y, scale, normal, planRotationRads);

  const filledSection = polyLine2Transform(
    switchedOutletFilledSectionPolygon(radius, circleCy, sectionBoundaryX),
    transform,
  );
  const circleOutline = polyLine2Transform(switchedOutletCircleOutline(radius, circleCy), transform);
  const slotLines = SWITCHED_OUTLET_SLOT_XS.map((slotX) =>
    polyLine2Transform(polyLine2ByTwoPoints(slotX * scale, lineTop, slotX * scale, lineBottom), transform),
  );

  const lineWidthPoints = sheetPointsNew(linewidth);

  return [
    sheetPolylineGeometryNew({
      paths: [circleOutline],
      fill: true,
      fillColor: fillLightColor,
      fillOpacity: percentNew(1),
      strokeColor,
      linewidth: lineWidthPoints,
    }),
    sheetPolygonGeometryNew({
      paths: [[filledSection]],
      fillColor: fillDarkColor,
      fillOpacity: percentNew(1),
    }),
    ...slotLines.map((slotLine) =>
      sheetPolylineGeometryNew({
        paths: [slotLine],
        fill: false,
        strokeColor,
        linewidth: lineWidthPoints,
      }),
    ),
  ];
}
